import {
  APP_NAME,
  KURULU_PAZARLAR,
  PAZAR_PROFILLERI,
  paraBiriminiAyarla,
  type PazarKodu,
  type PazarProfili,
} from '@amzjp/shared';

/**
 * Panelin pazarı (13 Ekim 2026, UK paneli). Açılışta `/api/v1/system/pazar`'dan
 * okunuyor; para gösterimi (`formatJpy`/`formatPara`), para işareti ve Amazon
 * bağlantıları buna göre. Uygulama cevap gelmeden çizilmiyor (bkz. `pazarYukle`);
 * buradaki JP yalnız çizim öncesi ve testler için.
 */
let etkin: PazarProfili = PAZAR_PROFILLERI.JP;

export function pazar(): PazarProfili {
  return etkin;
}

/** Uygulamanın adı her pazarda aynı (3 Kas 2026, sahip); hangi ülkede olunduğu ayrı gösteriliyor. */
export function uygulamaAdi(): string {
  return APP_NAME;
}

/**
 * Diğer pazarların panelleri (geçiş bağlantıları) — yalnız sunucuda KURULU olanlar: kodda
 * 21 pazar var, kurulmamış panelin adresi açılmaz (18 Ekim 2026).
 */
export function digerPaneller(): PazarProfili[] {
  return KURULU_PAZARLAR.filter((k) => k !== etkin.kod).map((k) => PAZAR_PROFILLERI[k]);
}

/** Ürün sayfası: JP amazon.co.jp, UK amazon.co.uk. */
export function pazarUrunBaglantisi(asin: string): string {
  return `https://www.${etkin.amazonAlanAdi}/dp/${asin}`;
}

/** Tek bir okuma denemesi: hata, zaman aşımı ya da tanınmayan pazar kodunda fırlatır. */
async function pazarKodunuOku(zamanAsimiMs: number): Promise<PazarKodu> {
  const kontrol = new AbortController();
  const zaman = setTimeout(() => kontrol.abort(), zamanAsimiMs);
  try {
    const cevap = await fetch('/api/v1/system/pazar', { signal: kontrol.signal, credentials: 'include' });
    if (!cevap.ok) throw new Error(`HTTP ${cevap.status}`);
    const govde = (await cevap.json()) as { kod?: string; data?: { kod?: string } };
    const kod = govde.data?.kod ?? govde.kod;
    if (!kod || !Object.hasOwn(PAZAR_PROFILLERI, kod)) throw new Error('Tanınmayan pazar kodu');
    return kod as PazarKodu;
  } finally {
    clearTimeout(zaman);
  }
}

/**
 * Uygulama çizilmeden önce bir kez çağrılır; sunucu pazarı söyleyene kadar BİTMEZ.
 *
 * ⚠️ Eskiden hata ya da 3 sn zaman aşımında JP ile açılıyordu. Tutarlar en küçük birimde olduğu
 * için yayında API yeniden başlarken açılan SA paneli oturum boyunca "SAR 1.234,56" yerine
 * "¥123.456" gösteriyor, bağlantılar amazon.co.jp'ye gidiyor, para kutusuna yazılan 120 de
 * 1,20 SAR olarak kaydediliyordu.
 *
 * Zaman aşımı her denemede uzuyor: sabit kalsaydı, yük altında bundan yavaş cevap veren sunucuya
 * panel hiç açılamazdı.
 *
 * @param baglantiYok Her başarısız denemede çağrılır (ekran "ulaşılamıyor" desin).
 */
export async function pazarYukle(baglantiYok?: () => void): Promise<void> {
  for (let deneme = 0; ; deneme++) {
    try {
      etkin = PAZAR_PROFILLERI[await pazarKodunuOku(Math.min(3_000 * 2 ** deneme, 30_000))];
      break;
    } catch {
      baglantiYok?.();
      await new Promise((tamam) => setTimeout(tamam, Math.min(1_000 * 2 ** deneme, 10_000)));
    }
  }
  paraBiriminiAyarla(etkin);
  // Sekme başlığı: iki panel aynı tarayıcıda açıkken karışmasın
  document.title = `${uygulamaAdi()} · ${etkin.ad}`;
}
